import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Minus, Plus, ShoppingBag, Truck, ArrowsClockwise, ShieldCheck, Star } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { ProductCard } from '@/components/product/ProductCard';
import { StarRating } from '@/components/product/StarRating';
import { useCart } from '@/context/CartContext';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { getProductById, PRODUCTS, getReviewsByProductId } from '@/data/products';

export default function ProductPage() {
  const { productId } = useParams();
  const [quantity, setQuantity] = useState(1);
  const [selectedColor, setSelectedColor] = useState(null);
  const [showStickyCart, setShowStickyCart] = useState(false);
  const { addToCart, loading: cartLoading } = useCart();

  // Get product from static data
  const product = getProductById(productId);
  const reviews = getReviewsByProductId(productId);
  
  // Get related products
  const relatedProducts = product 
    ? PRODUCTS.filter(p => p.category === product.category && p.id !== productId).slice(0, 4)
    : [];

  useEffect(() => {
    if (product?.colors?.length > 0) {
      setSelectedColor(product.colors[0]);
    }
  }, [product]);

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyCart(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleAddToCart = async () => {
    const success = await addToCart(product.id, quantity);
    if (success) {
      toast.success(`${product.name} added to cart!`);
    }
  };

  const handleBuyNow = async () => {
    const success = await addToCart(product.id, quantity);
    if (success) {
      window.location.href = '/checkout';
    }
  };

  if (!product) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 text-center">
        <h1 className="font-serif text-3xl text-nexa-charcoal mb-4">Product Not Found</h1>
        <Link to="/shop">
          <Button className="btn-primary">Continue Shopping</Button>
        </Link>
      </div>
    );
  }

  const discount = product.original_price 
    ? Math.round((1 - product.price / product.original_price) * 100)
    : null;

  return (
    <div data-testid="product-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
        {/* Breadcrumb */}
        <nav className="text-sm mb-8">
          <Link to="/" className="text-muted-foreground hover:text-nexa-charcoal">Home</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to={`/category/${product.category}`} className="text-muted-foreground hover:text-nexa-charcoal">{product.category}</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-nexa-charcoal">{product.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Product Image */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="aspect-square rounded-lg overflow-hidden bg-nexa-stone"
          >
            <img 
              src={product.images[0]}
              alt={product.name}
              className="w-full h-full object-cover"
              data-testid="product-image"
            />
          </motion.div>

          {/* Product Details */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div>
              <p className="text-nexa-gold text-sm uppercase tracking-widest font-semibold mb-2">
                {product.category}
              </p>
              <h1 className="font-serif text-3xl md:text-4xl font-medium text-nexa-charcoal mb-4" data-testid="product-title">
                {product.name}
              </h1>
              
              <div className="flex items-center gap-4 mb-4">
                <StarRating rating={product.rating} size={18} showCount count={product.reviews_count} />
              </div>

              <div className="flex items-center gap-4">
                <span className="font-serif text-3xl font-semibold text-nexa-charcoal" data-testid="product-price">
                  ${product.price.toFixed(2)}
                </span>
                {product.original_price && (
                  <>
                    <span className="text-xl text-muted-foreground line-through">
                      ${product.original_price.toFixed(2)}
                    </span>
                    <span className="bg-nexa-gold text-white text-sm font-semibold px-2 py-1 rounded">
                      Save {discount}%
                    </span>
                  </>
                )}
              </div>
            </div>

            <p className="text-muted-foreground leading-relaxed" data-testid="product-description">
              {product.description}
            </p>

            {/* Color Selection */}
            {product.colors && product.colors.length > 0 && (
              <div>
                <h3 className="font-semibold text-nexa-charcoal mb-3">Color: {selectedColor}</h3>
                <div className="flex gap-3">
                  {product.colors.map((color) => (
                    <button
                      key={color}
                      onClick={() => setSelectedColor(color)}
                      className={`px-4 py-2 border rounded-md text-sm transition-colors ${
                        selectedColor === color 
                          ? 'border-nexa-charcoal bg-nexa-charcoal text-white' 
                          : 'border-nexa-border hover:border-nexa-charcoal'
                      }`}
                      data-testid={`color-option-${color}`}
                    >
                      {color}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div>
              <h3 className="font-semibold text-nexa-charcoal mb-3">Quantity</h3>
              <div className="flex items-center gap-4">
                <div className="flex items-center border border-nexa-border rounded-md">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="quantity-btn"
                    data-testid="quantity-decrease"
                  >
                    <Minus size={16} />
                  </button>
                  <span className="w-12 text-center font-medium" data-testid="quantity-value">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="quantity-btn"
                    data-testid="quantity-increase"
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>
            </div>

            {/* Add to Cart / Buy Now */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                className="flex-1 h-14 btn-primary"
                onClick={handleAddToCart}
                disabled={cartLoading}
                data-testid="add-to-cart-btn"
              >
                <ShoppingBag size={20} className="mr-2" />
                Add to Cart
              </Button>
              <Button 
                className="flex-1 h-14 btn-gold"
                onClick={handleBuyNow}
                disabled={cartLoading}
                data-testid="buy-now-btn"
              >
                Buy Now
              </Button>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-3 gap-4 pt-6 border-t border-nexa-border">
              <div className="flex flex-col items-center text-center">
                <Truck size={24} className="text-nexa-gold mb-2" />
                <span className="text-xs font-medium text-nexa-charcoal">Free Shipping</span>
                <span className="text-xs text-muted-foreground">Orders $75+</span>
              </div>
              <div className="flex flex-col items-center text-center">
                <ArrowsClockwise size={24} className="text-nexa-gold mb-2" />
                <span className="text-xs font-medium text-nexa-charcoal">Easy Returns</span>
                <span className="text-xs text-muted-foreground">30-Day Policy</span>
              </div>
              <div className="flex flex-col items-center text-center">
                <ShieldCheck size={24} className="text-nexa-gold mb-2" />
                <span className="text-xs font-medium text-nexa-charcoal">Secure Checkout</span>
                <span className="text-xs text-muted-foreground">SSL Encrypted</span>
              </div>
            </div>

            {/* Product Specs */}
            {product.dimensions && (
              <div className="pt-6 border-t border-nexa-border">
                <h3 className="font-semibold text-nexa-charcoal mb-4">Product Details</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Dimensions:</span>
                    <span className="ml-2 text-nexa-charcoal">{product.dimensions}</span>
                  </div>
                  {product.materials && product.materials.length > 0 && (
                    <div>
                      <span className="text-muted-foreground">Materials:</span>
                      <span className="ml-2 text-nexa-charcoal">{product.materials.join(', ')}</span>
                    </div>
                  )}
                  {product.specifications && Object.entries(product.specifications).map(([key, value]) => (
                    <div key={key}>
                      <span className="text-muted-foreground">{key}:</span>
                      <span className="ml-2 text-nexa-charcoal">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </div>

        {/* Reviews Section */}
        {reviews.length > 0 && (
          <section className="mt-20" data-testid="reviews-section">
            <h2 className="font-serif text-3xl font-medium text-nexa-charcoal mb-8">Customer Reviews</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {reviews.map((review) => (
                <div key={review.id} className="bg-nexa-stone p-6 rounded-lg">
                  <div className="flex gap-1 mb-3">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} size={16} weight="fill" className="text-nexa-gold" />
                    ))}
                  </div>
                  <p className="text-nexa-charcoal mb-4 italic">"{review.comment}"</p>
                  <p className="font-semibold text-sm text-nexa-charcoal">{review.customer_name}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <section className="mt-20" data-testid="related-products">
            <h2 className="font-serif text-3xl font-medium text-nexa-charcoal mb-8">You May Also Like</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {relatedProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Sticky Add to Cart */}
      <div className={`sticky-cart ${showStickyCart ? 'visible' : ''} bg-white border-t border-nexa-border shadow-lg`}>
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src={product.images[0]} alt={product.name} className="w-16 h-16 object-cover rounded" />
            <div>
              <h3 className="font-medium text-nexa-charcoal">{product.name}</h3>
              <p className="font-serif text-lg font-semibold text-nexa-charcoal">${product.price.toFixed(2)}</p>
            </div>
          </div>
          <Button 
            className="btn-gold"
            onClick={handleAddToCart}
            disabled={cartLoading}
            data-testid="sticky-add-to-cart"
          >
            <ShoppingBag size={18} className="mr-2" />
            Add to Cart
          </Button>
        </div>
      </div>
    </div>
  );
}
